<template>
	<div
		style="padding-top: 5px; padding-left: 10px; padding-right: 10px; padding-bottom: 15px; flex: 1; display: flex">
		<vue2-tinymce-editor :options="tinymceConfig" v-model="content" class="sa-tinymce"></vue2-tinymce-editor>
	</div>
</template>

<script>
import { Vue2TinymceEditor } from "vue2-tinymce-editor";
export default {
	components: { Vue2TinymceEditor },
	data() {
		return {
			content: "",
			tinymceConfig: {
				skin: false,
				content_css: false,
				icons: "default",
				menubar: true,
				branding: true,
				plugins: "lists table image code",
				toolbar: "undo redo | alignleft aligncenter alignright alignjustify | table image | KBox KHeader KPageBreak KNumeroPagina | bold italic underline strikethrough forecolor backcolor | bullist numlist | removeformat | code",
				extended_valid_elements: "*[*]",
				custom_elements: "header,footer",

				// Permette di scegliere un'immagine dal PC dal dialog nativo "image"
				// (icona immagine in toolbar) e la incorpora in base64 nel contenuto,
				// senza bisogno di un endpoint di upload lato backend.
				file_picker_types: "image",

				// Immagini incollate / trascinate nell'editor: TinyMCE le crea come "blob:..."
				// (URL che esiste solo nel browser, il backend non puo' leggerlo).
				// Le convertiamo subito in base64 "data:image/...;base64,...".
				automatic_uploads: true,
				paste_data_images: true,
				images_upload_handler: function (blobInfo, success) {
					const dataUrl = "data:" + blobInfo.blob().type + ";base64," + blobInfo.base64();
					const isTinyMce5 = window.tinymce && Number(window.tinymce.majorVersion) < 6;
					if (isTinyMce5 && typeof success === "function") {
						success(dataUrl); // TinyMCE 5: callback
						return;
					}
					return Promise.resolve(dataUrl); // TinyMCE 6+: Promise
				},
				file_picker_callback: function (callback, value, meta) {
					if (meta.filetype !== "image") {
						return;
					}
					const input = document.createElement("input");
					input.setAttribute("type", "file");
					input.setAttribute("accept", "image/*");
					input.onchange = function () {
						const file = this.files[0];
						if (!file) {
							return;
						}
						const reader = new FileReader();
						reader.onload = function () {
							callback(reader.result, { alt: file.name });
						};
						reader.readAsDataURL(file);
					};
					input.click();
				},

				forced_root_block: "div",
				remove_trailing_brs: false,
				entity_encoding: "raw",

				content_style: `
    body { font-family: Arial, sans-serif; font-size: 14px; }
    .ft-checkbox-box { display: flex; margin: 10px 0; cursor: pointer; max-width:'max-content' }
    .ft-checkbox-box .ft-checkbox { display: inline-block; width: 15px; height: 15px; border: 2px solid #333; margin-right: 8px; vertical-align: middle; background-color: #fff; }
    .ft-checkbox-box[data-checked="true"] .ft-checkbox { background-color: #007bff; }
    .ft-page-break { display: block; height: 0; margin: 18px 0; border-top: 2px dashed #0056b3; position: relative; overflow: visible; }
    .ft-page-break::after { content: "Interruzione pagina"; position: absolute; top: -9px; left: 50%; transform: translateX(-50%); background: #fff; color: #0056b3; font-size: 11px; padding: 0 8px; }
    .ft-header-block { border-bottom: 2px solid #007bff; padding-bottom: 10px; margin-bottom: 15px; font-weight: bold; font-size: 16px; color: #333; }
  `,
				theme: "silver",
				setup(editor) {
					// Pulsante personalizzato Checklist
					editor.ui.registry.addButton("KBox", {
						text: "KBox",
						tooltip: "Inserisci una checkbox",
						onAction: () => {
							const boxHtml = `
                            <div class="ft-checkbox-box" data-checked="false">
                                <span class="ft-checkbox"></span>
                                <span class="ft-checkbox-label">text</span>
                            </div>
                            `;
							editor.insertContent(boxHtml);
						},
					});

					// Interruzione di pagina: nel PDF il contenuto successivo parte su una nuova pagina.
					// Nell'editor e' mostrata come linea tratteggiata (stile in content_style, non stampato).
					editor.ui.registry.addButton("KPageBreak", {
						text: "Interruzione pagina",
						tooltip: "Inserisci un'interruzione di pagina",
						onAction: () => {
							editor.insertContent('<div class="ft-page-break" style="page-break-after: always; font-size: 1px; line-height: 1px;">&#160;</div><div>&#160;</div>');
						},
					});

					// Numero pagina "Pagina X di Y" (toggle). Va nel piè di pagina ripetuto:
					// se esiste un ".piede-pagina" lo aggiunge in fondo, altrimenti ne crea uno solo per il numero.
					// Nel PDF "N" e "T" vengono sostituiti dal backend con pagina corrente e pagine totali.
					const NUMERO_PAGINA_HTML =
						'<div class="ft-numero-pagina" style="text-align: right; font-size: 7px; color: #64748b; margin-top: 2px;">Pagina <span class="ft-page">N</span> di <span class="ft-pages">T</span></div>';
					editor.ui.registry.addToggleButton("KNumeroPagina", {
						text: "N° pagina",
						tooltip: "Mostra/nascondi 'Pagina X di Y' in fondo a ogni pagina",
						onAction: (api) => {
							const dom = editor.dom;
							editor.undoManager.transact(() => {
								const esistenti = dom.select(".ft-numero-pagina");
								if (esistenti.length > 0) {
									esistenti.forEach((el) => {
										const parent = el.parentNode;
										dom.remove(el);
										if (parent && dom.hasClass(parent, "ft-piede-auto") && !parent.textContent.trim()) {
											dom.remove(parent);
										}
									});
									api.setActive(false);
								} else {
									const piede = dom.select(".piede-pagina")[0];
									if (piede) {
										piede.insertAdjacentHTML("beforeend", NUMERO_PAGINA_HTML);
									} else {
										editor.getBody().insertAdjacentHTML("afterbegin", '<div class="piede-pagina ft-piede-auto">' + NUMERO_PAGINA_HTML + "</div>");
									}
									api.setActive(true);
								}
							});
							editor.nodeChanged();
							editor.fire("change");
						},
						onSetup: (api) => {
							const aggiorna = () => api.setActive(editor.dom.select(".ft-numero-pagina").length > 0);
							editor.on("NodeChange SetContent", aggiorna);
							return () => editor.off("NodeChange SetContent", aggiorna);
						},
					});

					// Pulsante personalizzato Testata (Header) con immagine e intestazione
					editor.ui.registry.addButton("KHeader", {
						text: "Testata",
						tooltip: "Inserisci una testata con logo e intestazione",
						onAction: async () => {
							const origin = typeof window !== 'undefined' && window.location && window.location.origin ? window.location.origin : '';
							const baseUrl = typeof process !== 'undefined' && process.env && process.env.BASE_URL ? process.env.BASE_URL : '/';
							let defaultLogo = baseUrl;
							if (!defaultLogo.startsWith('http')) {
								defaultLogo = origin + (defaultLogo.startsWith('/') ? '' : '/') + defaultLogo;
							}
							if (!defaultLogo.endsWith('/')) {
								defaultLogo += '/';
							}
							defaultLogo += 'img/logo.png';
							// Il logo viene incorporato in base64 cosi' il backend lo trova nel PDF
							try {
								const blob = await (await fetch(defaultLogo)).blob();
								defaultLogo = await new Promise((resolve, reject) => {
									const reader = new FileReader();
									reader.onload = () => resolve(reader.result);
									reader.onerror = reject;
									reader.readAsDataURL(blob);
								});
							} catch (e) {
								console.warn("Logo non convertito in base64", e);
							}
							// Tabella (non flex): il generatore PDF supporta solo CSS 2.1
							const headerHtml = `
                            <table class="ft-header-block no-break" style="width: 100%; border-collapse: collapse; table-layout: fixed; border-bottom: 2px solid #007bff; margin-bottom: 15px;">
                                <tbody>
                                    <tr>
                                        <td style="width: 60%; vertical-align: middle; padding-bottom: 10px;">
                                            <img src="${defaultLogo}" alt="Logo" style="height: 50px; width: auto; vertical-align: middle; margin-right: 12px;" />
                                            <span style="display: inline-block; vertical-align: middle;">
                                                <strong style="font-size: 16px; color: #333;">NOME STRUTTURA SANITARIA</strong><br/>
                                                <span style="font-size: 12px; color: #666;">Indirizzo, Telefono, P.IVA / C.F.</span>
                                            </span>
                                        </td>
                                        <td style="width: 40%; text-align: right; vertical-align: middle; font-size: 12px; color: #444; padding-bottom: 10px;">
                                            <strong>MODULO DI CONSENSO INFORMATO</strong>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                            `;
							editor.insertContent(headerHtml);
						},
					});

					editor.on("click", function (e) {
						const box = e.target.closest(".ft-checkbox-box");
						if (!box) return;
						e.stopPropagation();
						const isLabelClick = e.target.classList.contains("ft-checkbox-label") || e.target.classList.contains("ft-checkbox");
						if (isLabelClick) {
							const checked = box.getAttribute("data-checked") === "true";
							box.setAttribute("data-checked", !checked);
						}
					});

					editor.on("dragover", function (e) {
						e.preventDefault();
						editor.focus();
					});

					editor.on("drop", function (e) {
						e.preventDefault();
						editor.focus();
						let data = e.dataTransfer.getData("text/html");
						if (data) {
							editor.insertContent(data);
							editor.nodeChanged();
							editor.focus();
						}
					});
				},
			},
		};
	},
	watch: {
		content() {
			this.$emit("updateContent", this.content);
		},
	},
	methods: {
		setContent(textContent) {
			this.content = textContent;
		},
	},
};
</script>

<style></style>